import { memo, useEffect, useTransition } from 'react'
import { Text, TouchableOpacity, View, StyleSheet } from 'react-native'
import { colorPalette, globalStyles } from '../../static/styles/global'
import Select from '../shared/form/Select'

// utils
import { convertTo24HourTime, daysOfWeek, mapSelectKey } from '../../utils'

// i18n
import { en as formSelectEn } from '../../i18n/FormSelect'
import ScheduleTimePicker from './ScheduleTimePicker'
import { useState } from 'react'
import AddNote from './AddNote'
import SelectKeyValue from '../shared/form/SelectKeyValue'

// components
import CustomButton from '../shared/button/Button'

// global types
import {
  AvailabilityDays,
  AvailabilityStatus,
  FridaySchedule,
  GlobalSchedule,
  MondaySchedule,
  SaturdaySchedule,
  SundaySchedule,
  ThursdaySchedule,
  TimeFormat,
  TuesdaySchedule,
  WednesdaySchedule
} from '../../types/scheduleTypes'
//translation
import { useTranslation } from 'react-i18next'

interface StartEndTimeProps {
  startTime: string
  startTimePickerVisible: any
  setStartTimePickerVisible: any
  endTime: string
  endTimePickerVisible: any
  setEndTimePickerVisible: any
}

const INIT_TIME = '00:00 am'

const StartEndTime = ({
  startTime,
  startTimePickerVisible,
  setStartTimePickerVisible,
  endTime,
  endTimePickerVisible,
  setEndTimePickerVisible
}: StartEndTimeProps): JSX.Element => {
  const { t } = useTranslation()
  return (
    <View style={[globalStyles.hDirection, globalStyles.mt2, globalStyles.mb1]}>
      <View style={[globalStyles.ml4, globalStyles.mr8]}>
        <Text
          style={[
            globalStyles.text,
            globalStyles.grayTextColor,
            globalStyles.fs11,
            globalStyles.mb1
          ]}
        >
          {t('starttime')}
        </Text>
        <TouchableOpacity
          style={[styles.timeUnderline]}
          onPress={() => {
            setStartTimePickerVisible(!startTimePickerVisible)
          }}
        >
          <Text
            style={[
              globalStyles.text,
              globalStyles.blackTextColor,
              globalStyles.fs12,
              globalStyles.mb1
            ]}
          >
            {startTime ? startTime : INIT_TIME}
          </Text>
        </TouchableOpacity>
      </View>
      {/* start time */}

      <View>
        <Text
          style={[
            globalStyles.text,
            globalStyles.grayTextColor,
            globalStyles.fs11,
            globalStyles.mb1
          ]}
        >
          {t('endtime')}
        </Text>
        <TouchableOpacity
          style={[styles.timeUnderline]}
          onPress={() => {
            setEndTimePickerVisible(!endTimePickerVisible)
          }}
          disabled={!startTime}
        >
          <Text
            style={[
              globalStyles.text,
              globalStyles.blackTextColor,
              !startTime && globalStyles.lightGrayTextColor,
              globalStyles.fs12,
              globalStyles.mb1
            ]}
          >
            {endTime ? endTime : INIT_TIME}
          </Text>
        </TouchableOpacity>
        {startTime && !endTime && (
          <Text
            style={[
              globalStyles.text,
              globalStyles.dangerTextColor,
              globalStyles.fs11,
              globalStyles.mt1
            ]}
          >
            {t('selectendtime')}
          </Text>
        )}
      </View>
      {/* end time */}
    </View>
  )
}

import { Schedule } from '../../types/globalTypes'
type AvailabilityCardProps = {
  day: AvailabilityDays
  schedules: Schedule[]
  addSecondShift: boolean
  setAddSecondShift: any
}

// store
import { useScheduleStore } from '../../stores/schedule'

const AvailabilityCard = memo(
  ({
    day,
    schedules,
    addSecondShift,
    setAddSecondShift
  }: AvailabilityCardProps): JSX.Element => {
    const [availability, setAvailability] = useState<AvailabilityStatus>('A')

    /***********************************************/
    /* LOCAL STATES
    /***********************************************/

    // note
    const [addNoteVisible, setAddNoteVisible] = useState(false)
    const [editNoteVisible, setEditNoteVisible] = useState(false)
    const [note, setNote] = useState(null)
    const [savedNote, setSavedNote] = useState(null)

    // first shift
    // const [addSecondShift, setAddSecondShift] = useState(false)
    const [startTime, setStartTime] = useState(null)
    const [endTime, setEndTime] = useState(null)
    const [startTimePickerVisible, setStartTimePickerVisible] = useState(false)
    const [endTimePickerVisible, setEndTimePickerVisible] = useState(false)
    const [startTimeFrom, setStartTimeFrom] = useState(0)
    const [endTimeFrom, setEndTimeFrom] = useState(0)

    const [selectedStartTimeItem, setSelectedStartTimeItem] = useState(null)
    const [selectedEndTimeItem, setSelectedEdnTimeItem] = useState(null)

    // second shift
    const [startSecondTimeShift, setStartSecondTimeShift] = useState(null)
    const [endSecondTimeShift, setEndSecondTimeShift] = useState(null)
    const [
      startSecondTimeShiftPickerVisible,
      setStartSecondTimeShiftPickerVisible
    ] = useState(false)
    const [
      endSecondTimeShiftPickerVisible,
      setEndSecondTimeShiftPickerVisible
    ] = useState(false)
    const [startSecondTimeShiftFrom, setStartSecondTimeShiftFrom] = useState(0)
    const [endSecondTimeShiftFrom, setEndSecondTimeShiftFrom] = useState(0)

    const [selectedStartSecondTimeShiftItem, setStartSecondTimeShiftItem] =
      useState(null)
    const [selectedEndSecondTimeShiftItem, setSelectedEdnSecondTimeShiftItem] =
      useState(null)

    // current schedule
    const [currentSchedule, setCurrentSchedule] = useState<
      | MondaySchedule
      | TuesdaySchedule
      | WednesdaySchedule
      | ThursdaySchedule
      | FridaySchedule
      | SaturdaySchedule
      | SundaySchedule
      | null
    >(null)

    /***********************************************/
    /* STORE DATA
    /***********************************************/

    // schedule store
    const { getSchedule, setSchedule } = useScheduleStore()

    /***********************************************/
    /* START HANDLERS
    /***********************************************/

    // handle set schedule day
    const handleSelectAvailability = (option: AvailabilityStatus) => {
      setAvailability(option)

      if (option === 'A') {
        setSchedule(day, 'A', null, null, null, null, null)
        console.log(day)
      } else if (option === 'S') {
        setSchedule(day, 'S', null, null, null, null, null)
      } else if (option === 'N') {
        setSchedule(day, 'N', null, null, null, null, null)
      }
    }

    // handle get schedule day
    const handleGetScheduleDay = (day: number) => {
      console.clear()
    }

    // handle set schedule day
    const handleSetScheduleDay = (
      day: number,
      availability: AvailabilityStatus,
      note: string,
      startTime: TimeFormat,
      endTime: TimeFormat,
      startSecondTimeShift: TimeFormat,
      endSecondTimeShift: TimeFormat
    ) => {
      // console.clear()
      setSchedule(
        day,
        availability,
        note,
        startTime,
        endTime,
        startSecondTimeShift,
        endSecondTimeShift
      )
    }

    // handle set time
    const handleSetTime = (
      sTime: string,
      eTime: string,
      sTime2: string,
      eTime2: string,
      time: number
    ) => {
      if (time === 1) {
        setStartTime(sTime)

        setEndTime(null)
        setStartSecondTimeShift(null)
        setEndSecondTimeShift(null)

        setSelectedEdnTimeItem(null)
        setStartSecondTimeShiftItem(null)
        setSelectedEdnSecondTimeShiftItem(null)
      }
      if (time === 2) {
        setEndTime(eTime)

        setStartSecondTimeShift(null)
        setEndSecondTimeShift(null)

        setStartSecondTimeShiftItem(null)
        setSelectedEdnSecondTimeShiftItem(null)
      }
      if (time === 3) {
        setStartSecondTimeShift(sTime2)

        setEndSecondTimeShift(null)

        setSelectedEdnSecondTimeShiftItem(null)
      }
      if (time === 4) {
        setEndSecondTimeShift(eTime2)
      }

      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(sTime),
        convertTo24HourTime(eTime),
        convertTo24HourTime(sTime2),
        convertTo24HourTime(eTime2)
      )
    }

    // handdle set add second shift
    const handdleSetAddSecondShift = (hasSecondShift: boolean) => {
      setAddSecondShift(hasSecondShift)

      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(startTime),
        convertTo24HourTime(endTime),
        convertTo24HourTime(null),
        convertTo24HourTime(null)
      )
    }

    // handle save note
    const handleSaveNote = (note: string) => {
      handleSetScheduleDay(day, availability, note, null, null, null, null)
    }

    const selectedSchedule = (
      day: number,
      currentSchedule: any
    ): GlobalSchedule | null => {
      let gSchedule = null

      if (day === 1) {
        gSchedule = {
          scheduleValue: currentSchedule.mondayScheduleValue,
          scheduleNAComment: currentSchedule.mondayScheduleNAComment,
          scheduleTime1Start: currentSchedule.mondayScheduleTime1Start,
          scheduleTime1End: currentSchedule.mondayScheduleTime1End,
          scheduleTime2Start: currentSchedule.mondayScheduleTime2Start,
          scheduleTime2End: currentSchedule.mondayScheduleTime2End
        }
      } else if (day === 2) {
        gSchedule = {
          scheduleValue: currentSchedule.tuesdayScheduleValue,
          scheduleNAComment: currentSchedule.tuesdayScheduleNAComment,
          scheduleTime1Start: currentSchedule.tuesdayScheduleTime1Start,
          scheduleTime1End: currentSchedule.tuesdayScheduleTime1End,
          scheduleTime2Start: currentSchedule.tuesdayScheduleTime2Start,
          scheduleTime2End: currentSchedule.tuesdayScheduleTime2End
        }
      } else if (day === 3) {
        gSchedule = {
          scheduleValue: currentSchedule.wednesdayScheduleValue,
          scheduleNAComment: currentSchedule.wednesdayScheduleNAComment,
          scheduleTime1Start: currentSchedule.wednesdayScheduleTime1Start,
          scheduleTime1End: currentSchedule.wednesdayScheduleTime1End,
          scheduleTime2Start: currentSchedule.wednesdayScheduleTime2Start,
          scheduleTime2End: currentSchedule.wednesdayScheduleTime2End
        }
      } else if (day === 4) {
        gSchedule = {
          scheduleValue: currentSchedule.thursdayScheduleValue,
          scheduleNAComment: currentSchedule.thursdayScheduleNAComment,
          scheduleTime1Start: currentSchedule.thursdayScheduleTime1Start,
          scheduleTime1End: currentSchedule.thursdayScheduleTime1End,
          scheduleTime2Start: currentSchedule.thursdayScheduleTime2Start,
          scheduleTime2End: currentSchedule.thursdayScheduleTime2End
        }
      } else if (day === 5) {
        gSchedule = {
          scheduleValue: currentSchedule.fridayScheduleValue,
          scheduleNAComment: currentSchedule.fridayScheduleNAComment,
          scheduleTime1Start: currentSchedule.fridayScheduleTime1Start,
          scheduleTime1End: currentSchedule.fridayScheduleTime1End,
          scheduleTime2Start: currentSchedule.fridayScheduleTime2Start,
          scheduleTime2End: currentSchedule.fridayScheduleTime2End
        }
      } else if (day === 6) {
        gSchedule = {
          scheduleValue: currentSchedule.saturdayScheduleValue,
          scheduleNAComment: currentSchedule.saturdayScheduleNAComment,
          scheduleTime1Start: currentSchedule.saturdayScheduleTime1Start,
          scheduleTime1End: currentSchedule.saturdayScheduleTime1End,
          scheduleTime2Start: currentSchedule.saturdayScheduleTime2Start,
          scheduleTime2End: currentSchedule.saturdayScheduleTime2End
        }
      } else if (day === 0) {
        gSchedule = {
          scheduleValue: currentSchedule.sundayScheduleValue,
          scheduleNAComment: currentSchedule.sundayScheduleNAComment,
          scheduleTime1Start: currentSchedule.sundayScheduleTime1Start,
          scheduleTime1End: currentSchedule.sundayScheduleTime1End,
          scheduleTime2Start: currentSchedule.sundayScheduleTime2Start,
          scheduleTime2End: currentSchedule.sundayScheduleTime2End
        }
      }

      return gSchedule
    }

    /***********************************************/
    /* SIDE EFFECTS
    /***********************************************/

    useEffect(() => {
      setNote('')

      // reset scheculing
      setSchedule(day, 'A', null, null, null, null, null)
    }, [])

    useEffect(() => {
      setCurrentSchedule(getSchedule(day))

      return () => {}
    }, [availability])
    const { t } = useTranslation()
    const dayss = daysOfWeek()
    return (
      <View>
        {currentSchedule && (
          <>
            <View
              style={[
                globalStyles.hDirection,
                globalStyles.hBetweenItems,
                globalStyles.vCenterItems
              ]}
            >
              <Text
                style={[
                  globalStyles.text,
                  globalStyles.fwBold,
                  globalStyles.fs18,
                  globalStyles.primaryTextColor,
                  globalStyles.flexOne
                ]}
              >
                {dayss[day]}
              </Text>
              <View>
                <SelectKeyValue
                  title={`${dayss[day]}` + t('availability')}
                  options={schedules}
                  selectedOption={mapSelectKey(schedules, 'A')}
                  onSelect={(option: AvailabilityStatus): void => {
                    handleSelectAvailability(option)
                  }}
                  status={{}}
                  textColor={'secondary'}
                />
              </View>
            </View>

            {/* start select action */}
            {false && (
              <View>
                <CustomButton
                  text="Set Schedule"
                  onPress={handleSetScheduleDay}
                  reverse
                  transparent
                />
                <CustomButton
                  text="Get Schedule"
                  onPress={() => handleGetScheduleDay(day)}
                  reverse
                  transparent
                />
              </View>
            )}
            {/* end select action */}

            {availability === schedules[1]['scheduleValue'] && (
              <>
                <View
                  style={[
                    globalStyles.hDirection,
                    globalStyles.hBetweenItems,
                    globalStyles.vStartItems,
                    globalStyles.mt1
                  ]}
                >
                  <StartEndTime
                    startTime={startTime}
                    startTimePickerVisible={startTimePickerVisible}
                    setStartTimePickerVisible={setStartTimePickerVisible}
                    endTime={endTime}
                    endTimePickerVisible={endTimePickerVisible}
                    setEndTimePickerVisible={setEndTimePickerVisible}
                  />
                  {!addSecondShift && (
                    <View>
                      <TouchableOpacity
                        onPress={() => {
                          setAddSecondShift(!addSecondShift)
                          setStartSecondTimeShift(null)
                          setEndSecondTimeShift(null)
                        }}
                      >
                        <Text
                          style={[
                            globalStyles.text,
                            globalStyles.fs12,
                            globalStyles.secondaryTextColor,
                            globalStyles.mt6
                          ]}
                        >
                          +{t('addsecondshift')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  {/* add second shift OR remove second shift */}
                </View>
                <ScheduleTimePicker
                  title={t('selectstarttime')}
                  visible={startTimePickerVisible}
                  setVisible={setStartTimePickerVisible}
                  onSelect={(time: string) => {
                    handleSetTime(
                      time,
                      endTime,
                      startSecondTimeShift,
                      endSecondTimeShift,
                      1
                    )
                  }}
                  setPickTimeFrom={setEndTimeFrom}
                  selectedItem={selectedStartTimeItem}
                  setSelectedItem={setSelectedStartTimeItem}
                />
                <ScheduleTimePicker
                  title={t('selectendtime')}
                  visible={endTimePickerVisible}
                  setVisible={setEndTimePickerVisible}
                  onSelect={(time: string) => {
                    handleSetTime(
                      startTime,
                      time,
                      startSecondTimeShift,
                      endSecondTimeShift,
                      2
                    )
                  }}
                  pickTimeFrom={endTimeFrom}
                  setPickTimeFrom={setStartSecondTimeShiftFrom}
                  selectedItem={selectedEndTimeItem}
                  setSelectedItem={setSelectedEdnTimeItem}
                />

                {addSecondShift && (
                  <>
                    <View style={[globalStyles.mt2]} />
                    <View
                      style={[
                        globalStyles.hDirection,
                        globalStyles.hBetweenItems,
                        globalStyles.vStartItems,
                        globalStyles.mt1
                      ]}
                    >
                      <StartEndTime
                        startTime={startSecondTimeShift}
                        startTimePickerVisible={
                          startSecondTimeShiftPickerVisible
                        }
                        setStartTimePickerVisible={
                          setStartSecondTimeShiftPickerVisible
                        }
                        endTime={endSecondTimeShift}
                        endTimePickerVisible={endSecondTimeShiftPickerVisible}
                        setEndTimePickerVisible={
                          setEndSecondTimeShiftPickerVisible
                        }
                      />
                      <View>
                        <TouchableOpacity
                          onPress={() => {
                            handdleSetAddSecondShift(!addSecondShift)
                          }}
                        >
                          <Text
                            style={[
                              globalStyles.text,
                              globalStyles.fs12,
                              globalStyles.dangerTextColor,
                              globalStyles.mt6
                            ]}
                          >
                            x{t('remove')}
                          </Text>
                        </TouchableOpacity>
                      </View>
                      {/* add second shift OR remove second shift */}
                    </View>
                    <ScheduleTimePicker
                      title={t('selectstarttime')}
                      visible={startSecondTimeShiftPickerVisible}
                      setVisible={setStartSecondTimeShiftPickerVisible}
                      onSelect={(time: string) => {
                        handleSetTime(
                          startTime,
                          endTime,
                          time,
                          endSecondTimeShift,
                          3
                        )
                      }}
                      pickTimeFrom={startSecondTimeShiftFrom}
                      setPickTimeFrom={setEndSecondTimeShiftFrom}
                      selectedItem={selectedStartSecondTimeShiftItem}
                      setSelectedItem={setStartSecondTimeShiftItem}
                    />
                    <ScheduleTimePicker
                      title={t('selectendtime')}
                      visible={endSecondTimeShiftPickerVisible}
                      setVisible={setEndSecondTimeShiftPickerVisible}
                      onSelect={(time: string) => {
                        handleSetTime(
                          startTime,
                          endTime,
                          startSecondTimeShift,
                          time,
                          4
                        )
                      }}
                      pickTimeFrom={endSecondTimeShiftFrom}
                      selectedItem={selectedEndSecondTimeShiftItem}
                      setSelectedItem={setSelectedEdnSecondTimeShiftItem}
                    />
                  </>
                )}
              </>
            )}
            {/* select availability times */}

            {availability === schedules[2]['scheduleValue'] && (
              <>
                <View
                  style={[
                    globalStyles.hDirection,
                    globalStyles.hBetweenItems,
                    globalStyles.vStartItems,
                    globalStyles.mt4
                  ]}
                >
                  <View style={[globalStyles.flexOne]}>
                    {savedNote && (
                      <Text
                        style={[
                          globalStyles.text,
                          globalStyles.fs12,
                          globalStyles.grayTextColor,
                          globalStyles.flexOne,
                          globalStyles.pr4
                        ]}
                      >
                        {t('note')} {savedNote.trim()}
                      </Text>
                    )}
                  </View>

                  {!savedNote && (
                    <TouchableOpacity
                      onPress={() => {
                        {
                          setAddNoteVisible(!addNoteVisible)
                        }
                      }}
                    >
                      <Text
                        style={[
                          globalStyles.text,
                          globalStyles.fs12,
                          globalStyles.secondaryTextColor
                        ]}
                      >
                        +{t('addnote')}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {/* open modal add note */}

                  {savedNote && (
                    <TouchableOpacity
                      onPress={() => {
                        {
                          setEditNoteVisible(!editNoteVisible)
                        }
                      }}
                    >
                      <Text
                        style={[
                          globalStyles.text,
                          globalStyles.fs12,
                          globalStyles.secondaryTextColor
                        ]}
                      >
                        {t('editnote')}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {/* open modal edit note */}
                </View>
                <AddNote
                  title={t('addnote')}
                  visible={addNoteVisible}
                  setVisible={setAddNoteVisible}
                  note={note}
                  setNote={setNote}
                  savedNote={savedNote}
                  setSavedNote={setSavedNote}
                  handleSaveNote={handleSaveNote}
                  mode="add"
                />
                <AddNote
                  title={t('editnote')}
                  visible={editNoteVisible}
                  setVisible={setEditNoteVisible}
                  note={note}
                  setNote={setNote}
                  savedNote={savedNote}
                  setSavedNote={setSavedNote}
                  handleSaveNote={handleSaveNote}
                  mode="edit"
                />
              </>
            )}
            {/* not available */}
          </>
        )}
      </View>
    )
  }
)

const styles = StyleSheet.create({
  timeUnderline: {
    alignSelf: 'flex-start',
    borderBottomColor: colorPalette.secondary,
    borderBottomWidth: 1
  }
})

export default AvailabilityCard
